import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  Select,
  Stack,
  Typography,
  Option,
} from '@mui/joy';
import {
  ExpandMore,
  ExpandLess,
  Security,
} from '@mui/icons-material';
import { permissionService } from '@/services/permissionService';
import { Permission, RoleWithPermissions } from '@/types/permission';
import toast from 'react-hot-toast';

interface RolePermissionsState {
  [role: string]: {
    [permissionId: number]: boolean;
  };
}

export default function AdminPermissionsPage() {
  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermissionsState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [bulkPermissions, setBulkPermissions] = useState<number[]>([]);

  // Group permissions by feature
  const permissionsByFeature = permissions.reduce((acc, permission) => {
    if (!acc[permission.feature]) {
      acc[permission.feature] = [];
    }
    acc[permission.feature].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesData, permissionsData, availableRolesData] = await Promise.all([
          permissionService.getRolesWithPermissions(),
          permissionService.getAllPermissions(),
          permissionService.getAvailableRoles(),
        ]);

        setRoles(rolesData);
        setPermissions(permissionsData);
        setAvailableRoles(availableRolesData);

        // Initialize role permissions state
        const initialRolePermissions: RolePermissionsState = {};
        availableRolesData.forEach(role => {
          initialRolePermissions[role] = {};
          permissionsData.forEach(permission => {
            // Check if role has this permission
            const hasPermission = rolesData.some(
              rp => rp.role === role && rp.permission_id === permission.id
            );
            initialRolePermissions[role][permission.id] = hasPermission;
          });
        });
        setRolePermissions(initialRolePermissions);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to load permissions data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePermissionToggle = async (role: string, permissionId: number, checked: boolean) => {
    try {
      if (checked) {
        await permissionService.grantPermissionToRole({
          role,
          permissionId,
        });
        toast.success('Permission granted to role successfully');
      } else {
        await permissionService.revokePermissionFromRole({
          role,
          permissionId,
        });
        toast.success('Permission revoked from role successfully');
      }

      // Update local state
      setRolePermissions(prev => ({
        ...prev,
        [role]: {
          ...prev[role],
          [permissionId]: checked,
        },
      }));
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update permission');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'manager': return 'warning';
      case 'employee': return 'success';
      default: return 'neutral';
    }
  };

  const formatFeatureName = (feature: string) => {
    return feature.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatActionName = (action: string) => {
    return action.charAt(0).toUpperCase() + action.slice(1);
  };

  const getRolePermissions = (role: string) => {
    return roles.filter(rp => rp.role === role);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading permissions...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography level="h1" mb={1}>Role Permission Management</Typography>
          <Typography level="body-sm" color="neutral">
            Manage feature access for different user roles
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        {availableRoles.map((role) => (
          <Card key={role}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography level="h4" mb={1}>
                    {role.charAt(0).toUpperCase() + role.slice(1)} Role
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="body-sm" color="neutral">
                      {getRolePermissions(role).length} permissions assigned
                    </Typography>
                    <Chip
                      size="sm"
                      color={getRoleColor(role)}
                      variant="soft"
                    >
                      {role}
                    </Chip>
                  </Stack>
                </Box>
                <IconButton
                  onClick={() => setExpandedRole(expandedRole === role ? null : role)}
                >
                  {expandedRole === role ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Stack>

              {expandedRole === role && (
                <Box>
                  <Grid container spacing={2}>
                    {Object.entries(permissionsByFeature).map(([feature, featurePermissions]) => (
                      <Grid xs={12} md={6} key={feature}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography level="title-lg" mb={2}>
                              {formatFeatureName(feature)}
                            </Typography>
                            <Stack spacing={1}>
                              {featurePermissions.map((permission) => (
                                <Stack
                                  key={permission.id}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ p: 1, borderRadius: 'sm', bgcolor: 'background.level0' }}
                                >
                                  <Box>
                                    <Typography level="body-sm" fontWeight="lg">
                                      {formatActionName(permission.action)}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                      {permission.description}
                                    </Typography>
                                  </Box>
                                  <Checkbox
                                    checked={rolePermissions[role]?.[permission.id] || false}
                                    onChange={(e) => handlePermissionToggle(role, permission.id, e.target.checked)}
                                    color="primary"
                                  />
                                </Stack>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
} 